// Share card text.
//
// Lives in the engine because it is a pure function of a finished run, and because the rules about
// what it must NOT contain are correctness constraints rather than presentation taste. The DOM side
// (clipboard, a button) belongs in the UI layer.
//
// SPOILER RULES — the share card is posted publicly by people who have already played, and read by
// people who have not:
//
//   * Never name the template or its id. Which layout today is only knowable by opening the game,
//     and "Big Dog 12 — The Chicane" hands that over.
//   * Never include the seed. It is the puzzle's identity and would let anyone rebuild the level.
//   * Never describe the route, the gate position, or the fences.
//
// Difficulty stars are safe and are the point of a curve nobody can otherwise see: they say how
// hard today was without saying what today is.

import type { Level, RunResult } from './types'
import type { ScoreBreakdown } from './score'

export const SHARE_URL = 'https://bigdog.loomskullcap.com'

/** m:ss — the share card is read at a glance, not audited. */
export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

/** ★★★☆☆ for difficulty 3. Conveys the day's rung without naming the layout. */
export function difficultyStars(difficulty: number): string {
  const filled = Math.max(0, Math.min(5, Math.round(difficulty)))
  return '★'.repeat(filled) + '☆'.repeat(5 - filled)
}

/**
 * Ten cells of how settled the flock stayed. This is the stat the game is actually about, and the
 * one a player cannot see while playing — the share card is where the penalty stops being a mystery.
 */
export function calmBar(meanStress: number): string {
  const calm = Math.max(0, Math.min(1, 1 - meanStress))
  const filled = Math.round(calm * 10)
  return '▓'.repeat(filled) + '░'.repeat(10 - filled)
}

export interface ShareInput {
  day: number
  level: Level
  result: RunResult
  score: ScoreBreakdown
}

/**
 * Faults as compact marks, for the share card.
 *
 * Deliberately terse and deliberately vague about the level: counts of what the handler did wrong
 * give a reader something to compare without telling them anything about today's paddock.
 */
export function faultMarks(result: RunResult): string {
  const f = result.faults
  const parts: string[] = []
  if (f.rushes) parts.push(`rush ×${f.rushes}`)
  if (f.bites) parts.push(`bite ×${f.bites}`)
  if (f.crosses) parts.push(`cross ×${f.crosses}`)
  if (f.penEscapes) parts.push(`out ×${f.penEscapes}`)
  return parts.join(' ')
}

export function shareText({ day, level, result, score }: ShareInput): string {
  const lines: string[] = []

  lines.push(`Big Dog ${day} ${difficultyStars(level.difficulty)} ${score.primary}/100`)

  if (result.won) {
    lines.push(
      `${formatDuration(score.durationMs)} · par ${formatDuration(
        level.parSeconds * 1000,
      )} · ${result.flockSize} sheep · ${result.barks} bark${result.barks === 1 ? '' : 's'}`,
    )
  } else {
    // An unfinished run says so plainly rather than quietly posting a low number.
    lines.push(`penned ${result.penned}/${result.flockSize} — did not finish`)
  }

  lines.push(`${calmBar(result.meanStress)} calm`)

  // The scorecard line, in the cadence of a trial sheet. Spoiler-free by construction: it says how
  // the run went and never what the layout was.
  const marks = faultMarks(result)
  if (marks) lines.push(marks)
  lines.push(SHARE_URL)

  return lines.join('\n')
}
